/**
 * Content Moderation Service using Gemini AI
 * Checks for adult/pornographic content in images and URLs
 */

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent';

/**
 * Check if an image contains adult/inappropriate content
 * @param {File|Blob} imageFile - The image file to check
 * @returns {Promise<{safe: boolean, reason?: string}>}
 */
export const checkImageContent = async (imageFile) => {
    if (!GEMINI_API_KEY) {
        console.warn('Gemini API key not configured, skipping content check');
        return { safe: true };
    }

    try {
        // Convert image to base64
        const base64Image = await fileToBase64(imageFile);
        const mimeType = imageFile.type || 'image/jpeg';

        const prompt = `Tu es un modérateur de contenu. Analyse cette image et détermine si elle contient du contenu inapproprié.

CRITÈRES DE REJET (réponds "UNSAFE" si l'un de ces critères est présent):
- Nudité totale ou partielle (seins, fesses, parties génitales visibles)
- Contenu sexuellement explicite ou suggestif
- Poses ou expressions à caractère érotique
- Sous-vêtements portés de manière provocante
- Contenu pornographique de toute nature
- Violence graphique ou gore
- Contenu haineux ou discriminatoire

CRITÈRES ACCEPTABLES:
- Photos professionnelles, portraits, photos de famille
- Maillots de bain dans un contexte approprié (plage, piscine)
- Publicités commerciales standards
- Logos, graphiques, illustrations professionnels

Réponds UNIQUEMENT avec un JSON valide:
{"safe": true} si l'image est acceptable
{"safe": false, "reason": "description courte du problème"} si l'image doit être rejetée`;

        const requestBody = {
            contents: [{
                parts: [
                    { text: prompt },
                    {
                        inline_data: {
                            mime_type: mimeType,
                            data: base64Image
                        }
                    }
                ]
            }],
            generationConfig: {
                temperature: 0.1,
                maxOutputTokens: 256
            }
        };

        const response = await fetch(GEMINI_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-goog-api-key': GEMINI_API_KEY,
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            console.error('Gemini API error:', await response.text());
            return { safe: true }; // Allow on API error to not block users
        }

        const data = await response.json();
        let generatedText = '';

        if (data.candidates?.[0]?.content?.parts?.[0]?.text) {
            generatedText = data.candidates[0].content.parts[0].text;
        }

        // Extract JSON from response
        const jsonMatch = generatedText.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
            const result = JSON.parse(jsonMatch[0]);
            return {
                safe: result.safe === true,
                reason: result.reason || undefined
            };
        }

        return { safe: true };

    } catch (error) {
        console.error('Error checking image content:', error);
        return { safe: true }; // Allow on error to not block users
    }
};

/**
 * Check if a URL points to adult/inappropriate content
 * @param {string} url - The URL to check
 * @returns {Promise<{safe: boolean, reason?: string}>}
 */
export const checkUrlContent = async (url) => {
    if (!GEMINI_API_KEY) {
        console.warn('Gemini API key not configured, skipping content check');
        return { safe: true };
    }

    try {
        const prompt = `Tu es un modérateur de contenu. Analyse cette URL et détermine si elle pointe vers un site avec du contenu inapproprié.

URL à analyser: ${url}

CRITÈRES DE REJET (réponds "UNSAFE" si l'un de ces critères est présent):
- Sites pornographiques ou pour adultes
- Sites de webcams ou de rencontres pour adultes
- Sites avec contenu sexuellement explicite
- Sites de vente de contenu pour adultes
- Sites promouvant la violence ou la haine
- Sites de jeux d'argent illégaux

CRITÈRES ACCEPTABLES:
- Sites d'entreprises légitimes
- Sites e-commerce standards
- Blogs, portfolios, sites personnels
- Sites d'information et actualités
- Sites de services professionnels

Utilise ta connaissance du web et du nom de domaine pour évaluer.

Réponds UNIQUEMENT avec un JSON valide:
{"safe": true} si le site semble acceptable
{"safe": false, "reason": "description courte du problème"} si le site doit être rejeté`;

        const requestBody = {
            contents: [{
                parts: [{ text: prompt }]
            }],
            tools: [{
                google_search: {}
            }],
            generationConfig: {
                temperature: 0.1,
                maxOutputTokens: 256
            }
        };

        const response = await fetch(GEMINI_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-goog-api-key': GEMINI_API_KEY,
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            console.error('Gemini API error:', await response.text());
            return { safe: true };
        }

        const data = await response.json();
        let generatedText = '';

        if (data.candidates?.[0]?.content?.parts?.[0]?.text) {
            generatedText = data.candidates[0].content.parts[0].text;
        }

        // Extract JSON from response
        const jsonMatch = generatedText.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
            const result = JSON.parse(jsonMatch[0]);
            return {
                safe: result.safe === true,
                reason: result.reason || undefined
            };
        }

        return { safe: true };

    } catch (error) {
        console.error('Error checking URL content:', error);
        return { safe: true };
    }
};

/**
 * Convert a File/Blob to base64 string
 * @param {File|Blob} file
 * @returns {Promise<string>}
 */
const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result.split(',')[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

/**
 * Check if content moderation is available
 * @returns {boolean}
 */
export const isModerationAvailable = () => {
    return !!GEMINI_API_KEY;
};

export default {
    checkImageContent,
    checkUrlContent,
    isModerationAvailable
};
